<script lang="ts">
  import { Mail, Archive, Eye } from '@lucide/svelte';
  import { Badge, Skeleton } from '@aatmanova/ui';
  import { createQuery, createMutation, useQueryClient } from '@tanstack/svelte-query';

  import { readable } from 'svelte/store';

  interface Props {
    client: any;
    addToast: (message: string, type?: 'info' | 'success' | 'warning' | 'error') => void;
  }

  let { client, addToast }: Props = $props();

  const queryClient = useQueryClient();

  let selectedMessage = $state<any>(null);

  let contactPage = $state(1);
  let contactStatus = $state('ALL');

  const STATUS_OPTIONS = ['ALL', 'UNREAD', 'READ', 'REPLIED', 'ARCHIVED'];

  function signalToStore<T>(getter: () => T) {
    return readable(getter(), (set) => {
      const cleanup = $effect.root(() => {
        $effect(() => {
          set(getter());
        });
      });
      return cleanup;
    });
  }

  const contactsQueryOptionsStore = signalToStore(() => ({
    queryKey: ['contacts', contactPage, contactStatus],
    queryFn: () => client.getContactMessages({
      page: contactPage,
      limit: 10,
      status: contactStatus === 'ALL' ? undefined : contactStatus,
    }),
  }));

  const contactsQuery = createQuery(contactsQueryOptionsStore);

  const updateStatusMutation = createMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      client.updateContactMessageStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      selectedMessage = null;
      addToast('Contact message status updated.', 'success');
    },
    onError: (err: any) => {
      addToast(err.message || 'Failed to update status.', 'error');
    },
  });

  const contacts = $derived($contactsQuery.data?.data || []);
  const contactsMeta = $derived($contactsQuery.data?.meta);
  const isLoading = $derived($contactsQuery.isLoading);
  const isError = $derived($contactsQuery.isError);
  const errorMessage = $derived($contactsQuery.error?.message || 'Failed to load messages');

  function setStatusFilter(status: string) {
    contactStatus = status;
    contactPage = 1;
  }

  function setPage(n: number) {
    contactPage = n;
  }

  function getPageNumbers(current: number, total: number) {
    const pages: (number | string)[] = [];
    if (total <= 7) {
      for (let i = 1; i <= total; i++) pages.push(i);
    } else {
      if (current <= 4) {
        pages.push(1, 2, 3, 4, 5, '...', total);
      } else if (current >= total - 3) {
        pages.push(1, '...', total - 4, total - 3, total - 2, total - 1, total);
      } else {
        pages.push(1, '...', current - 1, current, current + 1, '...', total);
      }
    }
    return pages;
  }

  function statusVariant(status: string) {
    if (status === 'UNREAD') return 'error';
    if (status === 'READ') return 'warning';
    if (status === 'REPLIED') return 'info';
    if (status === 'ARCHIVED') return 'neutral';
    return 'ghost';
  }
</script>

<div class="space-y-6">
  <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
    <div class="space-y-1">
      <h2 class="text-3xl font-extrabold tracking-tight text-base-content">Contact Messages</h2>
      <p class="text-sm text-base-content/70">Customer inquiries and spiritual consultation requests</p>
    </div>

    <div class="flex items-center gap-2 p-1 bg-base-200 rounded-xl border border-base-300 overflow-x-auto">
      {#each STATUS_OPTIONS as status}
        <button
          onclick={() => setStatusFilter(status)}
          class="whitespace-nowrap px-2.5 py-1.5 rounded-lg text-[11px] font-bold transition-all {contactStatus === status ? 'bg-accent text-accent-content shadow' : 'hover:bg-base-300 text-base-content/70'}"
        >
          {status === 'ALL' ? 'All' : status.charAt(0) + status.slice(1).toLowerCase()}
        </button>
      {/each}
    </div>
  </div>

  {#if isLoading}
    <div class="space-y-4">
      <Skeleton height="h-12" />
      <Skeleton height="h-16" />
      <Skeleton height="h-16" />
      <Skeleton height="h-16" />
    </div>
  {:else if isError}
    <div class="py-16 text-center border border-error/30 bg-error/5 rounded-xl">
      <p class="text-error font-bold text-sm">{errorMessage}</p>
      <p class="text-xs text-base-content/50 mt-2">Ensure the database is migrated and the API server is running.</p>
    </div>
  {:else if contacts.length === 0}
    <div class="py-20 text-center border border-dashed border-base-300 rounded-xl">
      <p class="text-base-content/50 font-medium">No contact messages recorded.</p>
    </div>
  {:else}
    <div class="hidden md:block bg-base-100 border border-base-300 rounded-xl overflow-hidden shadow-xl">
      <div class="overflow-x-auto">
        <table class="table table-zebra table-md w-full">
          <thead>
            <tr class="bg-base-200 text-xs font-semibold text-base-content/70 uppercase tracking-wider">
              <th>Name</th>
              <th>Email</th>
              <th>Subject</th>
              <th>Date</th>
              <th>Status</th>
              <th class="text-right">Actions</th>
            </tr>
          </thead>
          <tbody class="text-base-content/90">
            {#each contacts as msg}
              <tr class="hover {msg.status === 'UNREAD' ? 'font-bold' : ''}">
                <td>
                  <div class="flex items-center gap-2">
                    {#if msg.status === 'UNREAD'}
                      <span class="w-2 h-2 rounded-full bg-error"></span>
                    {/if}
                    <span class="text-sm">{msg.name}</span>
                  </div>
                </td>
                <td class="text-xs text-base-content/70">{msg.email}</td>
                <td class="text-xs text-base-content/80 max-w-[160px] truncate">{msg.subject || '—'}</td>
                <td class="text-xs font-semibold text-base-content/75">
                  {new Date(msg.createdAt).toLocaleDateString(undefined, { dateStyle: 'medium' })}
                </td>
                <td>
                  <Badge variant={statusVariant(msg.status)} size="xs" class="badge-outline font-bold uppercase tracking-wider">
                    {msg.status}
                  </Badge>
                </td>
                <td class="text-right">
                  <button
                    onclick={() => selectedMessage = msg}
                    class="btn btn-ghost btn-xs text-info hover:bg-info/10 font-bold flex items-center justify-center h-6 min-h-6 px-2.5 rounded-lg transition-all"
                  >
                    <Eye class="w-3.5 h-3.5 mr-1" /> View
                  </button>
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>

      {#if contactsMeta && contactsMeta.totalPages > 1}
        <div class="flex flex-col sm:flex-row items-center justify-between gap-4 bg-base-200/50 p-4 border-t border-base-300">
          <div class="text-xs font-bold text-base-content/50 uppercase tracking-wider">
            Page {contactPage} of {contactsMeta.totalPages} ({contactsMeta.total} total)
          </div>
          <div class="flex items-center gap-2">
            <button type="button" disabled={contactPage === 1} onclick={() => setPage(1)} class="btn btn-outline btn-xs font-bold border-base-300 disabled:opacity-30 hover:bg-base-200">First</button>
            <button type="button" disabled={contactPage === 1} onclick={() => setPage(contactPage - 1)} class="btn btn-outline btn-xs font-bold border-base-300 disabled:opacity-30 hover:bg-base-200">Prev</button>
            <div class="join">
              {#each getPageNumbers(contactPage, contactsMeta.totalPages) as p}
                {#if p === '...'}
                  <span class="join-item btn btn-xs btn-disabled font-bold border-base-300">...</span>
                {:else}
                  <button type="button" onclick={() => setPage(Number(p))} class="join-item btn btn-xs font-bold border-base-300 {contactPage === p ? 'btn-primary text-primary-content' : 'hover:bg-base-200'}">{p}</button>
                {/if}
              {/each}
            </div>
            <button type="button" disabled={contactPage === contactsMeta.totalPages} onclick={() => setPage(contactPage + 1)} class="btn btn-outline btn-xs font-bold border-base-300 disabled:opacity-30 hover:bg-base-200">Next</button>
            <button type="button" disabled={contactPage === contactsMeta.totalPages} onclick={() => setPage(contactsMeta.totalPages)} class="btn btn-outline btn-xs font-bold border-base-300 disabled:opacity-30 hover:bg-base-200">Last</button>
          </div>
        </div>
      {/if}
    </div>

    <div class="md:hidden space-y-4">
      {#each contacts as msg}
        <!-- svelte-ignore a11y_click_events_have_key_events -->
        <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
        <div
          class="card bg-base-100 border border-base-300 rounded-2xl p-4 shadow-sm hover:border-primary/30 transition-all duration-300 space-y-3 cursor-pointer"
          onclick={() => selectedMessage = msg}
        >
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2">
              {#if msg.status === 'UNREAD'}
                <span class="w-2 h-2 rounded-full bg-error"></span>
              {/if}
              <span class="font-bold text-sm text-base-content">{msg.name}</span>
            </div>
            <Badge variant={statusVariant(msg.status)} size="xs" class="badge-outline font-bold uppercase tracking-wider">{msg.status}</Badge>
          </div>
          <div class="text-xs text-base-content/70 truncate">{msg.email}</div>
          <div class="text-xs text-base-content/80 truncate">{msg.subject || 'No subject'}</div>
          <div class="text-[10px] text-base-content/50 font-semibold">
            {new Date(msg.createdAt).toLocaleDateString(undefined, { dateStyle: 'medium' })}
          </div>
        </div>
      {/each}

      {#if contactsMeta && contactsMeta.totalPages > 1}
        <div class="bg-base-100 border border-base-300 rounded-2xl p-4 flex items-center justify-between shadow-sm">
          <button type="button" disabled={contactPage === 1} onclick={() => setPage(contactPage - 1)} class="btn btn-outline btn-xs font-bold border-base-300 disabled:opacity-30 hover:bg-base-200">Prev</button>
          <div class="text-xs font-bold text-base-content">Page {contactPage} of {contactsMeta.totalPages}</div>
          <button type="button" disabled={contactPage === contactsMeta.totalPages} onclick={() => setPage(contactPage + 1)} class="btn btn-outline btn-xs font-bold border-base-300 disabled:opacity-30 hover:bg-base-200">Next</button>
        </div>
      {/if}
    </div>
  {/if}
</div>

{#if selectedMessage}
  <dialog class="modal modal-open z-50">
    <div class="modal-box bg-base-100 border border-base-300 shadow-2xl max-w-xl">
      <div class="flex items-center justify-between mb-4">
        <h3 class="font-bold text-lg text-base-content">Contact Message</h3>
        <button onclick={() => selectedMessage = null} class="btn btn-ghost btn-xs btn-circle">✕</button>
      </div>

      <div class="space-y-5">
        <div class="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span class="text-[10px] font-bold text-base-content/40 uppercase tracking-wider block mb-1">Name</span>
            <span class="font-bold text-base-content">{selectedMessage.name}</span>
          </div>
          <div>
            <span class="text-[10px] font-bold text-base-content/40 uppercase tracking-wider block mb-1">Email</span>
            <span class="text-base-content/80">{selectedMessage.email}</span>
          </div>
          <div>
            <span class="text-[10px] font-bold text-base-content/40 uppercase tracking-wider block mb-1">Subject</span>
            <span class="text-base-content/80">{selectedMessage.subject || '—'}</span>
          </div>
          <div>
            <span class="text-[10px] font-bold text-base-content/40 uppercase tracking-wider block mb-1">Date</span>
            <span class="text-base-content/80">{new Date(selectedMessage.createdAt).toLocaleDateString(undefined, { dateStyle: 'long' })}</span>
          </div>
        </div>

        <div>
          <span class="text-[10px] font-bold text-base-content/40 uppercase tracking-wider block mb-1">Message</span>
          <div class="bg-base-200/50 border border-base-300 rounded-xl p-4 text-sm text-base-content/90 leading-relaxed whitespace-pre-wrap">
            {selectedMessage.message}
          </div>
        </div>

        <div>
          <span class="text-[10px] font-bold text-base-content/40 uppercase tracking-wider block mb-1">Current Status</span>
          <Badge variant={statusVariant(selectedMessage.status)} size="sm" class="badge-outline font-bold uppercase tracking-wider">{selectedMessage.status}</Badge>
        </div>

        <div class="flex flex-wrap gap-2 pt-2 border-t border-base-300">
          {#if selectedMessage.status !== 'READ'}
            <button
              onclick={() => updateStatusMutation.mutate({ id: selectedMessage.id, status: 'READ' })}
              disabled={$updateStatusMutation.isPending}
              class="btn btn-sm btn-outline border-base-300 font-bold text-xs gap-1"
            >
              <Eye class="w-3.5 h-3.5" /> Mark Read
            </button>
          {/if}
          {#if selectedMessage.status !== 'REPLIED'}
            <button
              onclick={() => updateStatusMutation.mutate({ id: selectedMessage.id, status: 'REPLIED' })}
              disabled={$updateStatusMutation.isPending}
              class="btn btn-sm btn-outline border-info text-info font-bold text-xs gap-1"
            >
              <Mail class="w-3.5 h-3.5" /> Mark Replied
            </button>
          {/if}
          {#if selectedMessage.status !== 'ARCHIVED'}
            <button
              onclick={() => updateStatusMutation.mutate({ id: selectedMessage.id, status: 'ARCHIVED' })}
              disabled={$updateStatusMutation.isPending}
              class="btn btn-sm btn-outline border-base-300 font-bold text-xs gap-1 text-base-content/70"
            >
              <Archive class="w-3.5 h-3.5" /> Archive
            </button>
          {/if}
        </div>
      </div>

      <div class="modal-action">
        <button onclick={() => selectedMessage = null} class="btn btn-sm font-bold">Close</button>
      </div>
    </div>
    <button class="modal-backdrop bg-black/60 cursor-default" onclick={() => selectedMessage = null} aria-label="Close"></button>
  </dialog>
{/if}
